import React, { useState, useEffect, useCallback } from "react";
import "./BoyAnimation.css";

const BoyAnimation = ({ size = 45, speed = 500 }) => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [cachedImages, setCachedImages] = useState({});
  const totalFrames = 4; // stand1_0.png to stand1_3.png

  // Preload and cache images
  const preloadImages = useCallback(async () => {
    const cacheKey = 'boy-animation-images';
    const cacheVersion = 'v1';
    const fullCacheKey = `${cacheKey}-${cacheVersion}`;

    try {
      // Check if images are already cached in localStorage
      const cachedData = localStorage.getItem(fullCacheKey);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        setCachedImages(parsed);
        setImagesLoaded(true);
        console.log(`[BoyAnimation] Loaded from cache: ${Object.keys(parsed).length} images`);
        return;
      }

      console.log(`[BoyAnimation] Cache miss, loading images...`);
      
      // Preload images and convert to base64 for caching
      const imagePromises = [];
      const imageCache = {};

      for (let i = 0; i < totalFrames; i++) {
        const imagePath = `/boy_animation/stand1_${i}.png`;
        
        const promise = new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            // Convert image to base64 for localStorage
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const base64 = canvas.toDataURL('image/png');
            imageCache[i] = base64;
            console.log(`[BoyAnimation] Cached frame ${i}`);
            resolve();
          };
          img.onerror = (error) => {
            console.error(`[BoyAnimation] Failed to load frame ${i}:`, error);
            reject(error);
          };
          img.src = imagePath;
        });

        imagePromises.push(promise);
      }

      await Promise.all(imagePromises);

      // Cache images in localStorage
      localStorage.setItem(fullCacheKey, JSON.stringify(imageCache));
      setCachedImages(imageCache);
      setImagesLoaded(true);
      console.log(`[BoyAnimation] Successfully cached ${totalFrames} images`);
    } catch (error) {
      console.warn('Failed to cache boy animation images:', error);
      // Fallback to direct URLs without caching
      setImagesLoaded(true);
    }
  }, [totalFrames]);

  useEffect(() => {
    preloadImages();
  }, [preloadImages]);

  useEffect(() => {
    if (!imagesLoaded) return;

    const interval = setInterval(() => {
      setCurrentFrame((prev) => (prev + 1) % totalFrames);
    }, speed);

    return () => clearInterval(interval);
  }, [speed, totalFrames, imagesLoaded]);

  // Handle both px and percentage sizes
  const getSize = (sizeValue) => {
    if (typeof sizeValue === 'string') {
      return sizeValue;
    }
    return `${sizeValue}px`;
  };

  // Get current image source (cached base64 or fallback to URL)
  const getCurrentImageSrc = () => {
    const src = cachedImages[currentFrame] 
      ? cachedImages[currentFrame]
      : `/boy_animation/stand1_${currentFrame}.png`;
    return src;
  };

  console.log(`[BoyAnimation] imagesLoaded: ${imagesLoaded}, size: ${size}, currentFrame: ${currentFrame}`);

  if (!imagesLoaded) {
    // Show static first frame while loading
    console.log(`[BoyAnimation] Showing loading state with size: ${getSize(size)}`);
    return (
      <div
        className="boy-animation"
        style={{
          width: getSize(size),
          height: getSize(size),
          backgroundImage: `url(/boy_animation/stand1_0.png)`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      />
    );
  }

  return (
    <div
      className="boy-animation"
      style={{
        width: getSize(size),
        height: getSize(size),
        backgroundImage: `url(${getCurrentImageSrc()})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    />
  );
};

export default BoyAnimation;